import React, { useRef, useEffect } from 'react';
import './BubbleBackground.css';

function drawOctopus(ctx, x, y, size) {
  // Simple octopus: circle head + 4 tentacles
  ctx.save();
  ctx.beginPath();
  ctx.arc(x, y, size / 2.5, 0, 2 * Math.PI); // head
  ctx.fillStyle = '#fff';
  ctx.globalAlpha = 0.9;
  ctx.fill();
  ctx.globalAlpha = 1;
  // Tentacles
  for (let i = 0; i < 4; i++) {
    const angle = Math.PI / 4 + (i * Math.PI / 6);
    ctx.beginPath();
    ctx.moveTo(x, y + size / 2.5);
    ctx.lineTo(x + Math.cos(angle) * size * 0.7, y + size / 2.5 + Math.sin(angle) * size * 0.7);
    ctx.strokeStyle = '#fff';
    ctx.lineWidth = 1.2;
    ctx.stroke();
  }
  ctx.restore();
}

export default function BubbleBackground() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    let animationFrameId;
    const bubbles = [];
    const bubbleCount = 15 + Math.floor(Math.random() * 6); // 15 to 20 bubbles
    const margin = 2;
    const minRadius = 1.5; // 3px diameter
    const maxRadius = 2;   // 4px diameter

    // Octopus setup
    const octopusCount = 4;
    const octopusSize = 10;
    const octopuses = Array.from({ length: octopusCount }).map(() => ({
      x: Math.random() * (window.innerWidth - octopusSize) + octopusSize,
      y: window.innerHeight + Math.random() * 40,
      speed: 0.5 + Math.random() * 0.5,
      waveOffset: Math.random() * Math.PI * 2,
    }));

    function createBubble() {
      const radius = Math.random() < 0.5 ? minRadius : maxRadius; // 3px or 4px diameter
      return {
        x: Math.random() * (canvas.width - 2 * margin) + margin,
        y: canvas.height + radius + Math.random() * 40,
        r: radius,
        speed: (1 + Math.random() * 1.5) * 0.5, // half speed
        opacity: 0.7 + Math.random() * 0.3,
      };
    }

    function initBubbles() {
      bubbles.length = 0;
      for (let i = 0; i < bubbleCount; i++) {
        bubbles.push(createBubble());
      }
    }

    function drawOctopuses(frame) {
      for (let oct of octopuses) {
        // Wavy horizontal movement
        const wave = Math.sin(frame / 40 + oct.waveOffset) * 10;
        drawOctopus(ctx, oct.x + wave, oct.y, octopusSize);
        oct.y -= oct.speed;
        if (oct.y + octopusSize < 0) {
          oct.x = Math.random() * (canvas.width - octopusSize) + octopusSize;
          oct.y = canvas.height + Math.random() * 40;
          oct.speed = 0.5 + Math.random() * 0.5;
          oct.waveOffset = Math.random() * Math.PI * 2;
        }
      }
    }

    function draw(frame = 0) {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      // Draw bubbles
      for (let bubble of bubbles) {
        ctx.save();
        ctx.globalAlpha = bubble.opacity;
        ctx.beginPath();
        ctx.arc(bubble.x, bubble.y, bubble.r, 0, 2 * Math.PI);
        ctx.lineWidth = margin;
        ctx.strokeStyle = '#fff';
        ctx.stroke();
        ctx.restore();
        bubble.y -= bubble.speed;
        if (bubble.y + bubble.r < 0) {
          Object.assign(bubble, createBubble());
          bubble.y = canvas.height + bubble.r;
        }
      }
      // Draw octopuses
      drawOctopuses(frame);
      animationFrameId = requestAnimationFrame(() => draw(frame + 1));
    }

    function resizeCanvas() {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      initBubbles();
      // Reset octopuses positions
      octopuses.forEach(oct => {
        oct.x = Math.random() * (canvas.width - octopusSize) + octopusSize;
        oct.y = canvas.height + Math.random() * 40;
      });
    }

    resizeCanvas();
    draw();
    window.addEventListener('resize', resizeCanvas);
    return () => {
      window.removeEventListener('resize', resizeCanvas);
      cancelAnimationFrame(animationFrameId);
    };
  }, []);

  return (
    <div className="bubble-bg-wrapper">
      <canvas ref={canvasRef} className="bubble-bg-canvas" aria-hidden="true" />
    </div>
  );
}
